import { RefObject, useEffect, useRef, useState } from 'react';
import { UseFormRegister, UseFormSetValue } from 'react-hook-form';
import {
    Column,
    FlexBox,
    FluidImage,
    GridContainer,
    Icon,
    Row,
    SelectionSet,
    SelectionSetInput,
    Typography,
    useScreenClass,
} from '@vp/swan';
import { useIdentityContext } from '@99designs/design-services-common';
import { Maybe } from '@99designs/graph-utils/types';
import { __, getCurrentCulture } from '@99designs/i18n';
import { Format } from '@99designs/ninedown';
import { useCart } from '../../../../lib/hooks';
import { useBriefFormContext } from '../../../BriefContext/BriefFormContext';
import { FormInput } from '../../../BriefForm';
import { Field_GridChoiceField_Fragment } from '../../brief.generated';
import GridChoiceTile from './GridChoiceTile';
import isSwanIconKey from './isSwanIconKey';
import { useUpdateGridChoiceField } from './useUpdateGridChoiceField';

export type GridChoiceFieldProps = Field_GridChoiceField_Fragment & {
    register: UseFormRegister<FormInput>;
    setValue: UseFormSetValue<FormInput>;
};

export interface GridChoiceProps {
    index?: number;
    image: Maybe<string>;
    label: string;
    description?: Maybe<string>;
    optionDetails: string[];

    inputRef?: RefObject<HTMLInputElement>;

    isDisabled?: boolean;
    isViewOnly?: boolean;
    isCard?: boolean;
}

function GridChoice(props: GridChoiceProps) {
    return <GridChoiceTile {...props} />;
}

interface GridChoiceInputProps extends GridChoiceProps {
    id: string;
    index: number;
    value: string;

    register: UseFormRegister<FormInput>;
    required?: Maybe<boolean>;
    errorMessage?: Maybe<string>;

    isDisabled?: boolean;
    isCard?: boolean;
}

function GridChoiceInput(props: GridChoiceInputProps) {
    const {
        id,
        index,
        value,
        label,
        description,
        optionDetails,
        image,
        register,
        required,
        isDisabled,
        errorMessage,
        isCard,
    } = props;
    const itemRef = useRef(null);
    return (
        <SelectionSetInput
            key={value}
            {...register(id, {
                required:
                    required && !isDisabled ? errorMessage || __('This field is required') : false,
            })}
            ref={itemRef}
            value={value}
            disabled={isDisabled}
            aria-required={required ? true : undefined}
            style={{
                flex: isCard ? '0 0 auto' : undefined,
            }}
        >
            <GridChoice
                index={index}
                image={image}
                label={label}
                description={description}
                optionDetails={optionDetails}
                isDisabled={isDisabled}
                inputRef={itemRef}
                isCard={isCard}
            />
        </SelectionSetInput>
    );
}

export function Input(props: GridChoiceFieldProps) {
    const {
        id,
        required,
        options,
        register,
        setValue,
        choiceValue,
        immutable,
        errorMessage,
        variant,
    } = props;

    const DEFAULT_VALUE = options[0].value;
    const locale = getCurrentCulture();

    const { briefId } = useBriefFormContext();
    const { authorizationHeader, shopperId } = useIdentityContext();
    const { isInCart } = useCart(locale, { authorizationHeader, shopperId });
    const [selectedValue, setSelectedValue] = useState<string | null>(choiceValue || DEFAULT_VALUE);
    const updateMutation = useUpdateGridChoiceField(id, locale, briefId, choiceValue);
    const [isDisabled, setIsDisabled] = useState(false);
    const screenClass = useScreenClass();

    useEffect(() => {
        setValue(id, selectedValue);
        if (selectedValue) {
            updateMutation(selectedValue);
        }
    }, [id, setValue, selectedValue, updateMutation]);

    const isCardVariant = variant?.variantType === 'VARIANT_TYPE_CARD';
    const hasImages = options.some((o) => o.image != null);

    useEffect(() => {
        async function checkCartStatus() {
            if (briefId) {
                const inCart = await isInCart(briefId);
                setIsDisabled(!!immutable && inCart);
            }
        }

        void checkCartStatus();
    }, [briefId, immutable, choiceValue, isInCart]);

    const selectedOption = options.find((o) => o.value === selectedValue);

    return (
        <SelectionSet
            aria-label={id}
            variant="single-select"
            skin={isCardVariant || hasImages ? 'tiles-vertical' : 'simple-column'}
            selectedValue={selectedValue}
            onSelectedValueChange={(value) => setSelectedValue(value)}
        >
            <GridContainer>
                <FlexBox flexDirection={isCardVariant && screenClass === 'xs' ? 'column' : 'row'}>
                    {options?.map(({ value, label, image, description, optionDetails }, idx) => (
                        <GridChoiceInput
                            key={`grid-choice-input-${value}`}
                            id={id}
                            index={idx}
                            value={value}
                            label={label}
                            description={description}
                            optionDetails={optionDetails || []}
                            image={image}
                            register={register}
                            required={required}
                            errorMessage={errorMessage}
                            isDisabled={isDisabled}
                            isCard={isCardVariant}
                        />
                    ))}
                </FlexBox>
                {selectedOption && selectedOption.subtext && (
                    <Row>
                        <Column p={0} span={12}>
                            <div
                                key={selectedOption.value}
                                className="swan-text-small swan-px-1"
                                dangerouslySetInnerHTML={{
                                    __html: Format(selectedOption.subtext ?? ''),
                                }}
                            />
                        </Column>
                    </Row>
                )}
            </GridContainer>
        </SelectionSet>
    );
}

export function View(props: Field_GridChoiceField_Fragment) {
    const valueToChoiceData = Object.fromEntries(
        props.options.map((option) => {
            return [option.value, option];
        })
    );

    if (!props.choiceValue) {
        return null;
    }

    const { value } = valueToChoiceData[props.choiceValue];

    const hasImages = props.options.some((o) => o.image != null);

    return (
        <SelectionSet
            variant="single-select"
            skin={hasImages ? 'tiles-vertical' : 'simple-column'}
            selectedValue={value}
        >
            <GridContainer>
                <FlexBox>
                    {props.options?.map(({ value, label, image, description, optionDetails }) => (
                        <SelectionSetInput
                            disabled={props.choiceValue !== value}
                            key={value}
                            value={value}
                        >
                            <GridChoice
                                image={image}
                                label={label}
                                description={description}
                                optionDetails={optionDetails}
                                isViewOnly
                            />
                        </SelectionSetInput>
                    ))}
                </FlexBox>
            </GridContainer>
        </SelectionSet>
    );
}

export function Review(props: Field_GridChoiceField_Fragment) {
    const screenClass = useScreenClass();
    const valueToChoiceData = Object.fromEntries(
        props.options.map((option) => {
            return [option.value, option];
        })
    );

    if (!props.choiceValue) {
        return null;
    }

    const { image, label } = valueToChoiceData[props.choiceValue];

    return (
        <FlexBox
            style={isSwanIconKey(image) ? {} : { height: '24px', overflow: 'hidden' }}
            display="flex"
            alignItems={isSwanIconKey(image) ? 'center' : 'flex-start'}
        >
            {isSwanIconKey(image) ? (
                <Icon iconType={image} marginRight={3} size="standard" />
            ) : (
                image && (
                    <FluidImage
                        src={image}
                        alt={label}
                        style={{
                            objectFit: 'cover',
                            width: '32px',
                            height: screenClass === 'xs' ? '38px' : '46px',
                            objectPosition: 'left',
                        }}
                    />
                )
            )}
            <Typography fontSkin="body-standard">{label}</Typography>
        </FlexBox>
    );
}
